import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Open screens pick up a new release by themselves (owner, 1 Nov: a billing counter that stayed open all day kept
 * printing with the old bill layout after the fix went live). Every few minutes, and whenever the tab comes back, the
 * app reads /version.json (never cached); once a newer build is out it reloads at a quiet moment: the tab is hidden,
 * or nobody has touched it for a while, no dialog is open and nothing is printing. A bill being rung up survives the
 * reload (the till keeps its lines for the tab). Until then the staff bar offers "Refresh now".
 */
export const UPDATE_CHECK_MS = 3 * 60_000;
export const IDLE_BEFORE_RELOAD_MS = 45_000;
const RELOADED_KEY = 'cc.app.reloadedFor';

export interface QuietState {
  hidden: boolean;
  idleMs: number;
  dialogOpen: boolean;
  printing: boolean;
}

/** Reload now? Never in the middle of a dialog or a print; at once when nobody can see the tab, else after a pause. */
export function quietToReload(s: QuietState): boolean {
  if (s.dialogOpen || s.printing) return false;
  return s.hidden || s.idleMs >= IDLE_BEFORE_RELOAD_MS;
}

const currentBuild = (): string | null => (typeof __BUILD_ID__ === 'string' && __BUILD_ID__ ? __BUILD_ID__ : null);

function remember(key: string, value?: string): string | null {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    sessionStorage.setItem(key, value);
  } catch {
    /* private mode */
  }
  return null;
}

/** `ready`: a newer release is out and this screen has not loaded it yet. */
export function useAppUpdate(): { ready: boolean; reload: () => void } {
  const build = currentBuild();
  const [latest, setLatest] = useState<string | null>(null);
  const lastActivity = useRef(Date.now());
  const ready = !!build && !!latest && latest !== build;

  const reload = useCallback(() => {
    if (latest) remember(RELOADED_KEY, latest);
    window.location.reload();
  }, [latest]);

  // Ask the server which build is current: on a timer, when the tab is shown again and when the window gets focus.
  useEffect(() => {
    if (!build || import.meta.env.DEV) return;
    let stopped = false;
    const check = async () => {
      try {
        const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return;
        const v = (await res.json()) as { build?: unknown };
        if (!stopped && typeof v.build === 'string' && v.build) setLatest(v.build);
      } catch {
        /* offline: try again later */
      }
    };
    const onShow = () => {
      if (!document.hidden) void check();
    };
    const timer = setInterval(check, UPDATE_CHECK_MS);
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('focus', onShow);
    void check();
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('focus', onShow);
    };
  }, [build]);

  // Once a new build is out: reload at the first quiet moment, but only once per build (no reload loops).
  useEffect(() => {
    if (!ready || remember(RELOADED_KEY) === latest) return;
    const touch = () => {
      lastActivity.current = Date.now();
    };
    const events = ['pointerdown', 'keydown', 'touchstart', 'wheel'] as const;
    for (const e of events) window.addEventListener(e, touch, { passive: true });
    const timer = setInterval(() => {
      const quiet = quietToReload({
        hidden: document.hidden,
        idleMs: Date.now() - lastActivity.current,
        dialogOpen: !!document.querySelector('dialog[open]'),
        printing: document.body.classList.contains('printing-receipt'),
      });
      if (quiet) reload();
    }, 5000);
    return () => {
      clearInterval(timer);
      for (const e of events) window.removeEventListener(e, touch);
    };
  }, [ready, latest, reload]);

  return { ready, reload };
}
